<template>
  <ConfirmModal persist hide-actions wrapper-class="max-w-4xl" data-testid="iq-ticket-modal" @close-dialog="closeDialog">
    <template #title>
      <h1 class="p-6 text-headline-m">{{ t('TicketDetails.headline') }}</h1>
      <div class="flex flex-wrap justify-between px-6 pb-6">
        <div class="flex justify-start">
          <button class="btn-primary-color-s uppercase" data-testid="iq-edit-btn" :disabled="props.ticket.swapped" @click="goToEditForm">
            <SvgIcon :path="mdiPencilOutline" :size="18" class="mr-2" />
            {{ t('TicketDetails.CtaLabel.edit') }}
          </button>
          <MyOmrLink
            v-if="quotaConfig.showDownloadButton"
            :href="`/user_tickets/${toPlainId(props.ticket.id)}.pdf`"
            class="btn-secondary-color-s ml-4 uppercase"
          >
            <SvgIcon :path="mdiDownload" :size="18" class="mr-2" />
            {{ t('TicketDetails.CtaLabel.download') }}
          </MyOmrLink>
          <button class="btn-secondary-color-s ml-4 uppercase" data-testid="iq-resend-btn" @click="showResendTicketModal = true">
            <SvgIcon :path="mdiSend" :size="18" class="mr-2" />
            {{ t('TicketDetails.CtaLabel.send') }}
          </button>
        </div>
        <div class="flex justify-end">
          <span v-if="quotaConfig.showDeactivationFeature">
            <button
              v-if="!ticket.deactivatedAt"
              class="btn-link-neutral-m uppercase"
              data-testid="iq-detail-deactivate"
              @click="showDeactivateTicketsModal = true"
            >
              <SvgIcon :path="mdiCancel" :size="18" class="mr-2" />
              {{ t('TicketDetails.CtaLabel.deactivate') }}
            </button>
            <button
              v-else
              :disabled="props.isLimitReached || isDeadlineReached"
              class="btn-link-neutral-m uppercase"
              data-testid="iq-detail-activate"
              @click="showActivateTicketsModal = true"
            >
              <SvgIcon :path="mdiCancel" :size="18" class="mr-2" />
              {{ t('InvitationQuota.CrewQuota.activateTicketsModal.activateButton') }}
            </button>
          </span>
          <button
            class="btn-link-neutral-m uppercase"
            data-testid="iq-detail-delete"
            :disabled="props.ticket.swapped"
            @click="showDeleteTicketsModal = true"
          >
            <SvgIcon :path="mdiDelete" :size="18" class="mr-2" />
            {{ t('TicketDetails.CtaLabel.delete') }}
          </button>
        </div>
      </div>
    </template>
    <template #content>
      <div v-if="!props.loading && props.ticket" class="scrollbar-hide max-h-[550px] overflow-y-scroll px-6 pb-8">
        <AlertBox v-if="ticket.aasmState === 'unlocked'" :icon-path="mdiAlertOctagon" class="mb-6" type="danger">
          <template #header>{{ t('InvitationQuota.CrewQuota.invalidTickets.singleTicket.title') }}</template>
          {{ t('InvitationQuota.CrewQuota.invalidTickets.singleTicket.description') }}
        </AlertBox>
        <div class="flex items-start justify-between justify-items-start">
          <TableLight class="mr-12 w-full max-w-md table-auto">
            <tbody>
              <tr class="border-b border-main">
                <td class="min-w-min py-2 pr-12">
                  <strong>
                    {{ typed_t('ticketForm.salutation') }}
                  </strong>
                </td>
                <td class="py-2">{{ salutation || '' }}</td>
              </tr>

              <tr v-for="attribute in displayedTicketAttributes" :key="`ticket-attribute-${attribute}`" class="border-b border-main">
                <td class="min-w-min py-2 pr-12">
                  <strong>
                    {{ ticketAttributeLabel(attribute) }}
                  </strong>
                </td>
                <td class="py-2">{{ ticketAttributeValue(attribute) }}</td>
              </tr>
              <template v-if="quotaConfig.hasJobRelatedAttributes">
                <tr v-for="(value, attribute) in jobRelatedAttributes" :key="`ticket-attribute-${attribute}`" class="border-b border-main">
                  <td class="min-w-min py-2 pr-12">
                    <strong>
                      {{ typed_t(`ticketForm.${attribute}`) }}
                    </strong>
                  </td>
                  <td class="py-2">{{ value }}</td>
                </tr>
              </template>
            </tbody>
          </TableLight>
          <div>
            <QrcodeVue :value="props.ticket.identifier" :size="250" />
            <div class="mt-3 text-center">{{ ticket.identifier }}</div>
          </div>
        </div>
        <div v-if="visibleTicketFields.length" class="mt-6">
          <h2 class="mb-4 text-headline-s">Permissions</h2>
          <ul class="ml-8 w-full max-w-lg list-disc">
            <li v-for="field in visibleTicketFields" :key="`ticket-field-${field.key}`">
              <strong>
                {{ field.name }}
              </strong>
              <MarkdownContent :unstyled="true" class="enable-lists text-body-s" :md="getLocalizedFieldDescription(field)" />
            </li>
          </ul>
        </div>
      </div>

      <!-- Modals -->
      <DeactivateInternalTicketsModal v-if="showDeactivateTicketsModal" :ticket-ids="[ticket.id]" @close-dialog="handleDeactivateDialog" />
      <ActivateInternalTicketsModal v-if="showActivateTicketsModal" :ticket-ids="[ticket.id]" @close-dialog="handleActivateDialog" />
      <DeleteInternalTicketsModal v-if="showDeleteTicketsModal" :ticket-ids="[ticket.id]" @close-dialog="handleDeleteDialog" />
      <ResentTicketEmailModal v-if="showResendTicketModal" :ticket-id="ticket.id" @close-dialog="handleResendModal" />
    </template>
  </ConfirmModal>
</template>

<script lang="ts" setup>
import { mdiPencilOutline, mdiAlertOctagon, mdiDownload, mdiSend, mdiDelete, mdiCancel } from '@mdi/js'
import { MarkdownContent } from '@ramp106/omrjs-core-ui'
import QrcodeVue from 'qrcode.vue'
import { computed, ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import {
  useUserPropertiesPreferencesQuery,
  useCompanyFieldsByIndustryQuery,
  type TicketQuery,
  type InvitationQuotaQuery,
  type Field,
} from '@/gql/myomr'
import { toPlainId } from '@/helpers/globalIdHelper'
import typed_translation from '@/helpers/typed_translation'

import type { TabEnum } from './activeTab'
import {
  jobLevelsMapper,
  companyDepartmentsMapper,
  companySizesMapper,
  companyTypesMapper,
  companyIndustriesMapper,
  companyFieldsMapper,
  countriesMapper,
  salutationsMapper,
} from './formOptions'
import useQuotaConfig, { type InternalQuotaTypeEnum } from './quotaConfig'
import { isTicketRequest } from './ticketHelpers'

const props = defineProps<{
  currentActiveTab: TabEnum
  invitationQuota: InvitationQuotaQuery['invitationQuota']
  isLimitReached: boolean
  loading: boolean
  paymentInfoError?: string
  ticket: Ticket
}>()

const { t, te, locale } = useI18n()
const typed_t = typed_translation('InvitationQuota', props.invitationQuota.kind)

const showDeactivateTicketsModal = ref(false)
const showActivateTicketsModal = ref(false)
const showDeleteTicketsModal = ref(false)
const showResendTicketModal = ref(false)

function redirectBack() {
  router.push({ name: 'invitationQuota', query: route.query })
}

function goToEditForm() {
  router.push({ name: 'invitationQuotaEditTicket', query: route.query })
}

function shouldHidePermission(key: string | null | undefined): boolean {
  if (!key) return true
  return key.includes('OMR_MC') || key.includes('OMR_GT') || key.includes('festival_pass') || key == 'ffwd'
}

const visibleTicketFields = computed(() => props.ticket.fields?.filter((field) => !shouldHidePermission(field.key)) || [])

function handleDeactivateDialog(hasDeactivated: boolean) {
  showDeactivateTicketsModal.value = false
  if (hasDeactivated) redirectBack()
}

function handleActivateDialog(hasActivated: boolean) {
  showActivateTicketsModal.value = false
  if (hasActivated) redirectBack()
}

function handleDeleteDialog() {
  showDeleteTicketsModal.value = false
  redirectBack()
}

function handleResendModal() {
  showResendTicketModal.value = false
}

type Ticket = NonNullable<TicketQuery['ticket']>
type TicketAttribute = keyof Ticket

const route = useRoute()
const router = useRouter()

const quotaConfig = useQuotaConfig(props.invitationQuota.kind as InternalQuotaTypeEnum)

const isDeadlineReached = computed(() => {
  if (!props.invitationQuota.expirationDate) return false
  return new Date(props.invitationQuota.expirationDate).getTime() < new Date().getTime()
})

const getLocalizedFieldDescription = function (field: Field) {
  return locale.value === 'en' ? field.descriptionEn || '' : field.descriptionDe || ''
}

const { result, onResult } = useUserPropertiesPreferencesQuery()
const queryVariables = computed(() => ({ id: props.ticket.companyIndustryId ?? '' }))
const { result: companyFieldsQueryResult, onResult: onCompanyFieldsQueryResult } = useCompanyFieldsByIndustryQuery(queryVariables, () => ({
  enabled: !!props.ticket.companyIndustryId,
}))

const jobLevels = jobLevelsMapper(result)
const companyDepartments = companyDepartmentsMapper(result)
const companySizes = companySizesMapper(result)
const companyTypes = companyTypesMapper(result)
const companyIndustries = companyIndustriesMapper(result)
const companyFields = companyFieldsMapper(companyFieldsQueryResult)
const countries = countriesMapper(result)
const salutations = salutationsMapper(result)
const salutation = ref('')

const displayedTicketAttributes = computed<TicketAttribute[]>(() => {
  const commonAttributes: TicketAttribute[] = ['firstName', 'lastName', 'email', 'locale', 'companyName', 'companyType', 'position']
  return commonAttributes.concat(quotaConfig.value.additonalTicketAttributes)
})

const CompanyTypeTicketAttribute = computed(
  () => companyTypes.value.find((option) => option.value === props.ticket.companyType)?.text || '',
)
const localeTicketAttribute = computed(() => {
  if (!props.ticket.locale) return ''

  const key = `components.TicketDetailsFieldset.localeOptions.${props.ticket.locale}`
  return te(key) ? t(key) : props.ticket.locale
})

function ticketAttributeLabel(attribute: TicketAttribute) {
  const key = `components.TicketDetailsFieldset.${attribute}`
  return te(key) ? t(key) : typed_t(`ticketForm.${attribute}`)
}

function ticketAttributeValue(attribute: TicketAttribute) {
  if (attribute === 'companyType') return CompanyTypeTicketAttribute.value
  if (attribute === 'locale') return localeTicketAttribute.value
  return props.ticket[attribute]
}

const jobRelatedAttributes = reactive({
  jobLevel: '',
  companyDepartment: '',
  companySize: '',
  companyIndustry: '',
  companyField: '',
  country: '',
})
onResult(() => {
  jobRelatedAttributes['jobLevel'] = jobLevels.value.find((option) => option.value === props.ticket.jobLevel)?.text || ''
  jobRelatedAttributes['companyDepartment'] =
    companyDepartments.value.find((option) => option.value === props.ticket.companyDepartment)?.text || ''
  jobRelatedAttributes['companySize'] = companySizes.value.find((option) => option.value === props.ticket.companySize)?.text || ''
  jobRelatedAttributes['companyIndustry'] =
    companyIndustries.value.find((option) => option.value === props.ticket.companyIndustryId)?.text || ''
  jobRelatedAttributes['country'] = countries.value.find((option) => option.value === props.ticket.country)?.text || ''
  salutation.value = salutations.value.find((option) => option.value === props.ticket.salutation)?.text || ''
})
onCompanyFieldsQueryResult(() => {
  jobRelatedAttributes['companyField'] = companyFields.value.find((option) => option.value === props.ticket.companyFieldId)?.text || ''
})

const closeDialog = () => redirectBack()

onMounted(() => isTicketRequest(props.ticket) && redirectBack())
</script>
